// ======================================================================
// FILE:
// src/modules/finance/reports/trial_balance/pages/TrialBalancePage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Trial Balance
//
// PURPOSE:
// Read-only Trial Balance report.
//
// FEATURES:
// - Company filter
// - Financial Year filter
// - Financial Period filter
// - Account filter
// - Include zero-balance accounts
// - Opening debit / credit
// - Period debit / credit
// - Closing debit / credit
// - Balance status
// ======================================================================

import React, {
    useCallback,
    useMemo,
    useState,
} from "react";

import AsyncSelect
    from "react-select/async";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import FinanceSearchPanel
    from "../../../components/FinanceSearchPanel";

import trialBalanceService
    from "../../../services/trialBalanceService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function TrialBalancePage() {

    // ==============================================================
    // STATE
    // ==============================================================

    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        rows,
        setRows,
    ] = useState([]);


    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");


    const [
        reportData,
        setReportData,
    ] = useState(null);


    // ==============================================================
    // LOOKUP STATE
    // ==============================================================

    const [
        selectedCompany,
        setSelectedCompany,
    ] = useState(null);


    const [
        selectedFinancialYear,
        setSelectedFinancialYear,
    ] = useState(null);


    const [
        selectedFinancialPeriod,
        setSelectedFinancialPeriod,
    ] = useState(null);


    const [
        selectedAccount,
        setSelectedAccount,
    ] = useState(null);


    // ==============================================================
    // FILTERS
    // ==============================================================

    const [
        filters,
        setFilters,
    ] = useState({

        company_id: null,

        financial_year_id: null,

        financial_period_id: null,

        account_id: null,

        include_zero_balances: false,

    });


    // ==============================================================
    // UPDATE FILTER
    // ==============================================================

    const updateFilter = useCallback(
        (
            field,
            value
        ) => {

            setFilters(
                previous => ({
                    ...previous,
                    [field]: value,
                })
            );

        },
        []
    );


    // ==============================================================
    // COMPANY LOOKUP
    // ==============================================================

    const loadCompanies = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService.searchCompanies(
                    inputValue
                );

            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name ||
                        company.name ||
                        `Company ${company.id}`,

                    data:
                        company,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load companies:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // FINANCIAL YEAR LOOKUP
    // ==============================================================

    const loadFinancialYears = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService.searchFinancialYears(
                    inputValue
                );

            return (
                response || []
            ).map(
                year => ({

                    value:
                        year.id,

                    label:
                        year.year_name ||
                        year.name ||
                        `Financial Year ${year.id}`,

                    data:
                        year,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load financial years:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // FINANCIAL PERIOD LOOKUP
    // ==============================================================

    const loadFinancialPeriods = async (
        inputValue = ""
    ) => {

        if (
            !filters.financial_year_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService.searchFinancialPeriods(
                    filters.financial_year_id,
                    inputValue
                );

            return [
                    {
                        value: null,
                        label: "Full Financial Year",
                        data: null,
                    },

                    ...(response || []).map(
                        period => ({

                            value:
                                period.id,

                            label:
                                period.period_name ||
                                period.period_code ||
                                `Period ${period.id}`,

                            data:
                                period,

                        })
                    ),
                ];

        } catch (error) {

            console.error(
                "Failed to load financial periods:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // ACCOUNT LOOKUP
    // ==============================================================

    const loadAccounts = async (
        inputValue = ""
    ) => {

        if (
            !filters.company_id
        ) {

            return [];

        }


        try {

            return await financeLookupService
                .searchChartOfAccounts(
                    filters.company_id,
                    inputValue,
                    null,
                    20
                );

        } catch (error) {

            console.error(
                "Failed to load chart of accounts:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // LOAD TRIAL BALANCE
    // ==============================================================

    const loadTrialBalance = async () => {

        setLoading(true);

        setErrorMessage("");


        try {

            const response =
                await trialBalanceService
                    .getTrialBalance(
                        filters
                    );


            setReportData(
                response || null
            );


            setRows(
                response?.rows || []
            );

        } catch (error) {

            console.error(
                "Failed to load Trial Balance:",
                error
            );


            setReportData(
                null
            );


            setRows(
                []
            );


            setErrorMessage(
                error?.response?.data?.message ||
                "Unable to load Trial Balance."
            );

        } finally {

            setLoading(false);

        }

    };


    // ==============================================================
    // SEARCH
    // ==============================================================

    const handleSearch = () => {

        if (
            !filters.company_id
        ) {

            setErrorMessage(
                "Please select Company."
            );

            return;

        }


        if (
            !filters.financial_year_id
        ) {

            setErrorMessage(
                "Please select Financial Year."
            );

            return;

        }


        


        loadTrialBalance();

    };


    // ==============================================================
    // RESET
    // ==============================================================

    const handleReset = () => {

        setFilters({

            company_id: null,

            financial_year_id: null,

            financial_period_id: null,

            account_id: null,

            include_zero_balances: false,

        });


        setSelectedCompany(
            null
        );


        setSelectedFinancialYear(
            null
        );


        setSelectedFinancialPeriod(
            null
        );


        setSelectedAccount(
            null
        );


        setReportData(
            null
        );


        setRows(
            []
        );


        setErrorMessage(
            ""
        );

    };


    // ==============================================================
    // FORMAT AMOUNT
    // ==============================================================

    const formatAmount = (
        value
    ) => {

        const amount =
            Number(
                value || 0
            );


        return amount.toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        );

    };


    // ==============================================================
    // SUMMARY CARDS
    // ==============================================================

    const summaryCards = useMemo(
        () => [

            {
                title:
                    "Opening Debit",

                value:
                    formatAmount(
                        reportData?.opening?.debit
                    ),

            },

            {
                title:
                    "Opening Credit",

                value:
                    formatAmount(
                        reportData?.opening?.credit
                    ),

            },

            {
                title:
                    "Closing Debit",

                value:
                    formatAmount(
                        reportData?.closing?.debit
                    ),

            },

            {
                title:
                    "Closing Credit",

                value:
                    formatAmount(
                        reportData?.closing?.credit
                    ),

            },

        ],
        [
            reportData
        ]
    );


    // ==============================================================
    // RENDER
    // ==============================================================

    return (

        <div className="container-fluid">

            {/* ======================================================
                HEADER
            ====================================================== */}

            <FinancePageHeader
                title="Trial Balance"
                subtitle="Trial Balance Report"
            />


            {/* ======================================================
                SUMMARY
            ====================================================== */}

            <FinanceSummaryCards
                cards={
                    summaryCards
                }
            />


            {/* ======================================================
                FILTER PANEL
            ====================================================== */}

            <FinanceSearchPanel
                loading={
                    loading
                }
                onSearch={
                    handleSearch
                }
                onReset={
                    handleReset
                }
            >

                {/* ==================================================
                    COMPANY
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Company

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadCompanies
                        }
                        isClearable
                        value={
                            selectedCompany
                        }
                        onChange={
                            option => {

                                setSelectedCompany(
                                    option
                                );


                                setSelectedAccount(
                                    null
                                );


                                updateFilter(
                                    "company_id",
                                    option?.value || null
                                );


                                updateFilter(
                                    "account_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    FINANCIAL YEAR
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Financial Year

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialYears
                        }
                        isClearable
                        value={
                            selectedFinancialYear
                        }
                        onChange={
                            option => {

                                setSelectedFinancialYear(
                                    option
                                );


                                setSelectedFinancialPeriod(
                                    null
                                );


                                updateFilter(
                                    "financial_year_id",
                                    option?.value || null
                                );


                                updateFilter(
                                    "financial_period_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    FINANCIAL PERIOD
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">
                        Financial Period
                        <span className="text-muted ms-1">
                            (Optional)
                        </span>
                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialPeriods
                        }
                        isClearable
                        isDisabled={
                            !filters.financial_year_id
                        }
                        value={
                            selectedFinancialPeriod
                        }
                        onChange={
                            option => {

                                setSelectedFinancialPeriod(
                                    option
                                );


                                updateFilter(
                                    "financial_period_id",
                                    option?.value || null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    ACCOUNT
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Account

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadAccounts
                        }
                        isClearable
                        isDisabled={
                            !filters.company_id
                        }
                        value={
                            selectedAccount
                        }
                        onChange={
                            option => {

                                setSelectedAccount(
                                    option
                                );


                                updateFilter(
                                    "account_id",
                                    option?.value || null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    INCLUDE ZERO BALANCES
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <div className="form-check mt-4">

                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="includeZeroBalances"
                            checked={
                                filters.include_zero_balances
                            }
                            onChange={
                                event =>
                                    updateFilter(
                                        "include_zero_balances",
                                        event.target.checked
                                    )
                            }
                        />

                        <label
                            className="form-check-label"
                            htmlFor="includeZeroBalances"
                        >

                            Include Zero Balance Accounts

                        </label>

                    </div>

                </div>

            </FinanceSearchPanel>


            {/* ======================================================
                ERROR
            ====================================================== */}

            {
                errorMessage && (

                    <div className="alert alert-danger mt-3">

                        {errorMessage}

                    </div>

                )
            }


            {/* ======================================================
                BALANCE STATUS
            ====================================================== */}

            {
                reportData && (

                    <div className="card mt-3">

                        <div className="card-body">

                            <div
                                className={
                                    reportData.is_balanced
                                        ? "alert alert-success mb-0"
                                        : "alert alert-danger mb-0"
                                }
                            >

                                <strong>

                                    Trial Balance:

                                </strong>

                                {" "}

                                {
                                    reportData.is_balanced
                                        ? "BALANCED"
                                        : "NOT BALANCED"
                                }

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ======================================================
                TRIAL BALANCE TABLE
            ====================================================== */}

            <div className="card mt-3">

                <div className="card-header">

                    <strong>
                        Trial Balance
                    </strong>

                </div>


                <div className="card-body p-0">

                    <div className="table-responsive">

                        <table
                            className={
                                "table " +
                                "table-hover " +
                                "table-bordered " +
                                "mb-0"
                            }
                        >

                            <thead>

                                <tr>

                                    <th>
                                        Account Code
                                    </th>

                                    <th>
                                        Account Name
                                    </th>

                                    <th>
                                        Nature
                                    </th>

                                    <th className="text-end">
                                        Opening Debit
                                    </th>

                                    <th className="text-end">
                                        Opening Credit
                                    </th>

                                    <th className="text-end">
                                        Period Debit
                                    </th>

                                    <th className="text-end">
                                        Period Credit
                                    </th>

                                    <th className="text-end">
                                        Closing Debit
                                    </th>

                                    <th className="text-end">
                                        Closing Credit
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    loading ? (

                                        <tr>

                                            <td
                                                colSpan="9"
                                                className={
                                                    "text-center " +
                                                    "py-4"
                                                }
                                            >

                                                Loading Trial Balance...

                                            </td>

                                        </tr>

                                    ) : rows.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan="9"
                                                className={
                                                    "text-center " +
                                                    "py-4"
                                                }
                                            >

                                                No Trial Balance data found.

                                            </td>

                                        </tr>

                                    ) : (

                                        rows.map(
                                            (
                                                row,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        row.account_id ||
                                                        index
                                                    }
                                                >

                                                    <td>

                                                        {
                                                            row.account_code ||
                                                            "-"
                                                        }

                                                    </td>


                                                    <td>

                                                        {
                                                            row.account_name ||
                                                            "-"
                                                        }

                                                    </td>


                                                    <td>

                                                        {
                                                            row.account_nature ||
                                                            "-"
                                                        }

                                                    </td>


                                                    <td
                                                        className="text-end"
                                                    >

                                                        {
                                                            formatAmount(
                                                                row.opening_debit
                                                            )
                                                        }

                                                    </td>


                                                    <td
                                                        className="text-end"
                                                    >

                                                        {
                                                            formatAmount(
                                                                row.opening_credit
                                                            )
                                                        }

                                                    </td>


                                                    <td
                                                        className="text-end"
                                                    >

                                                        {
                                                            formatAmount(
                                                                row.period_debit
                                                            )
                                                        }

                                                    </td>


                                                    <td
                                                        className="text-end"
                                                    >

                                                        {
                                                            formatAmount(
                                                                row.period_credit
                                                            )
                                                        }

                                                    </td>


                                                    <td
                                                        className="text-end"
                                                    >

                                                        {
                                                            formatAmount(
                                                                row.closing_debit
                                                            )
                                                        }

                                                    </td>


                                                    <td
                                                        className="text-end"
                                                    >

                                                        {
                                                            formatAmount(
                                                                row.closing_credit
                                                            )
                                                        }

                                                    </td>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>


                            {/* ==================================================
                                TOTALS
                            ================================================== */}

                            {
                                reportData && (

                                    <tfoot>

                                        <tr>

                                            <th
                                                colSpan="3"
                                                className="text-end"
                                            >

                                                Total

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        reportData
                                                            ?.opening
                                                            ?.debit
                                                    )
                                                }

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        reportData
                                                            ?.opening
                                                            ?.credit
                                                    )
                                                }

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        reportData
                                                            ?.period_movement
                                                            ?.debit
                                                    )
                                                }

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        reportData
                                                            ?.period_movement
                                                            ?.credit
                                                    )
                                                }

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        reportData
                                                            ?.closing
                                                            ?.debit
                                                    )
                                                }

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        reportData
                                                            ?.closing
                                                            ?.credit
                                                    )
                                                }

                                            </th>

                                        </tr>

                                    </tfoot>

                                )
                            }

                        </table>

                    </div>

                </div>

            </div>


            {/* ======================================================
                DIFFERENCE DETAILS
            ====================================================== */}

            {
                reportData && (

                    <div className="card mt-3">

                        <div className="card-body">

                            <div className="row">

                                <div className="col-md-4">

                                    <strong>
                                        Opening Difference
                                    </strong>

                                    <div>

                                        {
                                            formatAmount(
                                                reportData
                                                    ?.opening
                                                    ?.difference
                                            )
                                        }

                                    </div>

                                </div>


                                <div className="col-md-4">

                                    <strong>
                                        Period Difference
                                    </strong>

                                    <div>

                                        {
                                            formatAmount(
                                                reportData
                                                    ?.period_movement
                                                    ?.difference
                                            )
                                        }

                                    </div>

                                </div>


                                <div className="col-md-4">

                                    <strong>
                                        Closing Difference
                                    </strong>

                                    <div>

                                        {
                                            formatAmount(
                                                reportData
                                                    ?.closing
                                                    ?.difference
                                            )
                                        }

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                )
            }

        </div>

    );

}